"use client";

import { useState, useEffect, useMemo, startTransition } from "react";
import {
  getCalendarMonth,
  getCalendarUpcoming,
  getCalendarToday,
  getCalendarEkadashi,
  type CalendarFestival,
  type CalendarTodayResponse,
} from "@/lib/api";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import {
  CalendarTodayCard,
  CalendarGrid,
  FestivalCard,
  EkadashiSection,
  MONTH_NAMES,
  formatDateLong,
} from "@/components/calendar";

export default function CalendarClient() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [todayData, setTodayData] = useState<CalendarTodayResponse | null>(null);
  const [monthData, setMonthData] = useState<{ festivals: CalendarFestival[] } | null>(null);
  const [upcoming, setUpcoming] = useState<CalendarFestival[]>([]);
  const [ekadashis, setEkadashis] = useState<CalendarFestival[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [ekadashiYear, setEkadashiYear] = useState(now.getFullYear());

  // Fetch today + upcoming on mount — `now` is stable (module-scope const), no re-fetch needed
  useEffect(() => {
    Promise.all([
      getCalendarToday(),
      getCalendarUpcoming(30),
      getCalendarEkadashi(now.getFullYear()),
    ])
      .then(([today, up, ek]) => {
        setTodayData(today);
        setUpcoming(up.festivals);
        setEkadashis(ek.ekadashis);
        trackCalculatorUsed({ calculator: "calendar" });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch month data
  useEffect(() => {
    startTransition(() => setLoading(true));
    getCalendarMonth(year, month)
      .then((data) => {
        startTransition(() => {
          setMonthData(data);
          setLoading(false);
        });
      })
      .catch(() => startTransition(() => setLoading(false)));
  }, [year, month]);

  // Fetch ekadashi when year changes
  useEffect(() => {
    getCalendarEkadashi(ekadashiYear)
      .then((data) => setEkadashis(data.ekadashis))
      .catch(() => {});
  }, [ekadashiYear]);

  const changeMonth = (delta: number) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    if (newYear >= 2026 && newYear <= 2027) {
      setMonth(newMonth);
      setYear(newYear);
      setSelectedDate(null);
    }
  };

  // Events for selected date
  const selectedEvents = useMemo(() => {
    if (!selectedDate || !monthData) return [];
    return monthData.festivals.filter((f) => f.date === selectedDate);
  }, [selectedDate, monthData]);

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12">
      {/* Today Card */}
      {todayData && <CalendarTodayCard data={todayData} />}

      <MonthNavigation year={year} month={month} onChangeMonth={changeMonth} />

      <MonthGrid
        year={year}
        month={month}
        loading={loading}
        festivals={monthData?.festivals || []}
        onSelect={setSelectedDate}
      />

      <SelectedDateEvents selectedDate={selectedDate} selectedEvents={selectedEvents} />

      <MonthEvents year={year} month={month} festivals={monthData?.festivals || []} />

      <UpcomingEvents upcoming={upcoming} />

      <EkadashiTracker
        ekadashiYear={ekadashiYear}
        ekadashis={ekadashis}
        onYearChange={setEkadashiYear}
      />

      <div className="mt-12">
        <CalculatorCrossLinks currentPage="calendar" />
      </div>
    </div>
  );
}

function MonthNavigation({
  year,
  month,
  onChangeMonth,
}: {
  year: number;
  month: number;
  onChangeMonth: (delta: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mb-6">
      <button
        type="button"
        onClick={() => onChangeMonth(-1)}
        disabled={year === 2026 && month === 1}
        className="px-4 py-2 rounded-lg border border-ish-border bg-white text-ish-dark text-sm font-semibold hover:bg-ish-accent/5 disabled:opacity-30 disabled:hover:bg-white transition-colors"
      >
        Prev
      </button>
      <h2 className="font-serif text-xl font-bold text-ish-dark m-0">
        {MONTH_NAMES[month - 1]} {year}
      </h2>
      <button
        type="button"
        onClick={() => onChangeMonth(1)}
        disabled={year === 2027 && month === 12}
        className="px-4 py-2 rounded-lg border border-ish-border bg-white text-ish-dark text-sm font-semibold hover:bg-ish-accent/5 disabled:opacity-30 disabled:hover:bg-white transition-colors"
      >
        Next
      </button>
    </div>
  );
}

function MonthGrid({
  year,
  month,
  loading,
  festivals,
  onSelect,
}: {
  year: number;
  month: number;
  loading: boolean;
  festivals: CalendarFestival[];
  onSelect: (date: string) => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-ish-border p-4 shadow-sm mb-8">
      {loading ? (
        <div className="text-center py-20 text-ish-text-muted font-medium">
          <div className="inline-block w-6 h-6 border-2 border-ish-accent/30 border-t-ish-accent rounded-full animate-spin mb-2" />
          <p>Loading calendar...</p>
        </div>
      ) : (
        <CalendarGrid year={year} month={month} festivals={festivals} onSelect={onSelect} />
      )}
    </div>
  );
}

function SelectedDateEvents({
  selectedDate,
  selectedEvents,
}: {
  selectedDate: string | null;
  selectedEvents: CalendarFestival[];
}) {
  if (!selectedDate || selectedEvents.length === 0) return null;
  return (
    <div className="mt-8 animate-in fade-in slide-in-from-bottom-2">
      <h3 className="text-base font-bold text-ish-dark mb-4 px-1">
        {formatDateLong(selectedDate)}
      </h3>
      <div className="flex flex-col gap-3">
        {selectedEvents.map((festival) => (
          <FestivalCard key={festival.id} festival={festival} />
        ))}
      </div>
    </div>
  );
}

function MonthEvents({
  year,
  month,
  festivals,
}: {
  year: number;
  month: number;
  festivals: CalendarFestival[];
}) {
  if (festivals.length === 0) return null;
  return (
    <div className="mt-12">
      <h2 className="font-serif text-xl font-bold text-ish-dark mb-6">
        All Events in {MONTH_NAMES[month - 1]} {year}
      </h2>
      <div className="flex flex-col gap-3">
        {festivals.map((festival) => (
          <FestivalCard key={festival.id + festival.date} festival={festival} />
        ))}
      </div>
    </div>
  );
}

function UpcomingEvents({ upcoming }: { upcoming: CalendarFestival[] }) {
  if (upcoming.length === 0) return null;
  return (
    <div className="mt-12">
      <h2 className="font-serif text-xl font-bold text-ish-dark mb-6">
        Upcoming Festivals (Next 30 Days)
      </h2>
      <div className="flex flex-col gap-3">
        {upcoming.map((festival) => (
          <FestivalCard key={festival.id + festival.date} festival={festival} />
        ))}
      </div>
    </div>
  );
}

function EkadashiTracker({
  ekadashiYear,
  ekadashis,
  onYearChange,
}: {
  ekadashiYear: number;
  ekadashis: CalendarFestival[];
  onYearChange: (year: number) => void;
}) {
  return (
    <div className="mt-12 border-t border-ish-border pt-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="font-serif text-xl font-bold text-ish-dark m-0">Ekadashi Tracker</h2>
        <div className="flex p-1 bg-black/[0.03] rounded-lg self-start">
          {[2026, 2027].map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => onYearChange(year)}
              className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${
                ekadashiYear === year
                  ? "bg-white text-ish-dark shadow-sm"
                  : "text-ish-text-muted hover:text-ish-dark"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
      <EkadashiSection ekadashis={ekadashis} />
    </div>
  );
}
